import Select from 'components/Common/Form/Input/Select'
import { useCurrencyFormatter, useDecimalFormatter } from 'hooks/useNumberFormatters'
import { useCallback, useMemo } from 'react'
import type { ChangeEvent } from 'react'
import { useIntl } from 'react-intl'

interface Props {
  offers: Array<App.LoyaltyPurchasePointsOffer>
  selectedOfferId: string | undefined
  onSelect: (offerId: string) => void
  disabled?: boolean
  label?: string
}

function LoyaltyPurchasePointsOfferSelect(props: Props) {
  const { offers, selectedOfferId, onSelect, disabled, label } = props
  const intl = useIntl()
  const formatDecimal = useDecimalFormatter()
  const formatCurrency = useCurrencyFormatter()

  const handleChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    onSelect(event.target.value)
  }, [onSelect])

  const labelText = label ?? intl.formatMessage({
    id: 'ygwQCYhlyGQEgXUFvy4n',
    defaultMessage: 'Select amount',
    description: 'Label for the points-amount selector on the Société Buy Points landing page',
  })

  const placeholderText = intl.formatMessage({
    id: 'nWHMI5iBnsx0O9SI8owL',
    defaultMessage: 'Choose an amount',
    description: 'Placeholder for the points-amount selector when no offer is yet picked',
  })

  const options = useMemo(() =>
    offers.map(offer => ({
      id: offer.id,
      label: intl.formatMessage(
        {
          id: '-_3f5bdwUh7WFTdQNO31',
          defaultMessage: '{points} pts ({price})',
          description: 'Single option in the points-amount selector — points count and currency price for one offer tier',
        },
        {
          points: formatDecimal(offer.basePoints),
          price: formatCurrency(offer.publicPrice),
        },
      ),
      disabled: offer.isEligibleNow === false,
    })),
  [offers, intl, formatDecimal, formatCurrency])

  return <Select
    label={labelText}
    name="loyaltyPurchasePointsOffer"
    value={selectedOfferId ?? ''}
    onChange={handleChange}
    disabled={disabled}
    placeholder={placeholderText}
    optionalIndicatorEnabled={false}
  >
    <option value="" disabled>{placeholderText}</option>
    {options.map(option => (
      <option key={option.id} value={option.id} disabled={option.disabled}>{option.label}</option>
    ))}
  </Select>
}

export default LoyaltyPurchasePointsOfferSelect
